import { PROMOTION_TYPE } from "@components/features/AutoPromotionPopup/constants";
import { dispatchAnalyticsEvent } from "@configs/analytics/dispatch";
import { resolveCsChannel } from "@configs/analytics/events/channel";
import { EVENTS, EventName, GAME_SOURCE } from "@configs/analytics/events/constants";
import {
  hasSeenAndMarkCloseDeposit,
  hasSeenAndMarkContactCsEntryShown,
  hasSeenAndMarkDeposit,
  hasSeenAndMarkHeaderShown,
  hasSeenAndMarkOncePerSession,
  hasSeenAndMarkPwaInstallSuccess,
  hasSeenAndMarkRegistrationFormFocus,
  isValidEvent,
} from "@configs/analytics/events/dedupe";
import { DepositFunnelTracker, RegistrationFunnelTracker } from "@configs/analytics/events/funnel";
import {
  AuthSource,
  CashbackMiniPromptShownMetadata,
  ChannelSelectionMode,
  ClickHomeBannerMetadata,
  ClickMiniBannerMetadata,
  ClickPromotionItemMetadata,
  ClickPromotionPageMetadata,
  CloseDepositIframeMetadata,
  CloseDepositNativeMetadata,
  ContactCsEntryShownMetadata,
  CsChannelMetadata,
  CsClickMetadata,
  CsFlowStep,
  CsSource,
  DepositCreateResultOptions,
  DepositGroupsEmptyMetadata,
  DepositIframeCloseReason,
  DepositMethodsEmptyMetadata,
  DepositNativeCloseReason,
  DepositSource,
  DepositStep,
  EventMetadata,
  FtdCountdownMetadata,
  GameExitParams,
  GoogleAuthSource,
  HeaderShownMetadata,
  LanguageChangeSource,
  NotificationAction,
  NotificationBrowserPromptResponseMetadata,
  NotificationBrowserPromptShownMetadata,
  NotificationInAppPromptResponseMetadata,
  NotificationInAppPromptShownMetadata,
  NotificationInAppSource,
  NotificationSubscriptionStatusMetadata,
  OTPFailedAttemptMetadata,
  OTPMethod,
  OTPSource,
  OpenGetMoreBallsTabMetadata,
  PromotionScreenContext,
  PwaInstallMethod,
  PwaInstallSource,
  PwaInstallSuccessTrigger,
  RedirectAutocloseResultMetadata,
  RegistrationFormFocusMetadata,
  RegistrationMethod,
  RegistrationStep,
  ShareChannel,
  SlipUploadAttemptMetadata,
  SlipUploadFailedMetadata,
  SurpriseMeEventMetadata,
  UsernameSource,
  UsernameSuggestionTrigger,
  VipQuickDepositClickedMetadata,
  VipQuickDepositMetadata,
  VipQuickDepositSkippedMetadata,
  VipQuickDepositSubmittedMetadata,
  WithdrawalSource,
} from "@configs/analytics/events/types";
import { runWhenProvidersReady } from "@configs/analytics/providers";
import { envConfig } from "@configs/env";
import { NotificationPermissionState, UserMode } from "@types";
import { getStoredCredential } from "@utils/credentials";
import { getApiErrorMessage } from "@utils/error-resolvers";
import logger from "@utils/logger";
import { getReferralCode, resolveReferralCode } from "@utils/referral";
import {
  GameModel,
  LoginGrantType,
  MemberSettingAuthMode,
  WalletTxMethod,
  WithdrawRequest,
  WithdrawResponse,
} from "kz-ui-sdk";
import type {
  NudgeModalPresentedMetadata,
  PromotionActionMetadata,
  PromotionClaimResultMetadata,
  PromotionIssuedMetadata,
  PromotionPresentedMetadata,
  PromotionQueueEndedMetadata,
} from "./events/promotion";

// ===== Core dispatcher =====

export type TrackFn = (eventName: EventName | string, metadata?: EventMetadata, value?: number) => void;

/**
 * Shared dispatch core: validates event name (dev warning), then forwards to
 * the active provider via `dispatchAnalyticsEvent`. Used as the `track`
 * callback by both `useAnalyticsTracking` and `AnalyticsTracker`.
 */
export const dispatchTrackedEvent: TrackFn = (eventName, metadata, value) => {
  if (!isValidEvent(eventName) && !eventName.startsWith("act:") && !eventName.startsWith("evt:")) {
    logger._console.warn(`Event name "${eventName}" doesn't follow naming convention (act: or evt:)`);
  }
  dispatchAnalyticsEvent(eventName, metadata, value);
};

// ===== Registration =====

export function trackRegisterClick(track: TrackFn, source: AuthSource): void {
  const referralCode = getReferralCode();
  const tracker = RegistrationFunnelTracker.getInstance();
  tracker.startRegistration();
  tracker.addStep(RegistrationStep.CLICK_REGISTER);

  track(EVENTS.CLICK_REGISTER, {
    source,
    step: RegistrationStep.CLICK_REGISTER,
    referralCode,
    hasReferralCode: !!referralCode,
  });
}

export function trackRegisterFormFocus(track: TrackFn, method: RegistrationMethod): void {
  if (hasSeenAndMarkRegistrationFormFocus(method)) return;
  const metadata: RegistrationFormFocusMetadata = {
    method,
    pathname: window.location.pathname,
    fromPathname:
      (window.history?.state as { usr?: { from?: { pathname?: string } } } | null)?.usr?.from?.pathname ?? undefined,
    search: window.location.search ?? undefined,
  };
  track(EVENTS.REGISTRATION_FORM_FOCUS, metadata);
}

export function trackRegisterFormSubmit(
  track: TrackFn,
  method: RegistrationMethod,
  options?: { timeSpentOnForm?: number; retryCount?: number; usernameSource?: UsernameSource },
): void {
  const { code: referralCode, source: referralCodeSource } = resolveReferralCode();
  const tracker = RegistrationFunnelTracker.getInstance();
  tracker.addStep(RegistrationStep.FORM_SUBMIT);
  const retryCount = tracker.incrementAttempt("register_form_submit");

  track(EVENTS.REGISTER_FORM_SUBMIT, {
    method,
    step: RegistrationStep.FORM_SUBMIT,
    timeSpentOnForm: options?.timeSpentOnForm,
    retryCount: options?.retryCount || retryCount,
    referralCode,
    hasReferralCode: !!referralCode,
    referralCodeSource,
    usernameSource: options?.usernameSource,
  });
}

export function trackUsernameSuggestionShown(
  track: TrackFn,
  params: { trigger: UsernameSuggestionTrigger; suggestionCount: number },
): void {
  track(EVENTS.USERNAME_SUGGESTION_SHOWN, params);
}

export function trackUsernameSuggestionSelected(
  track: TrackFn,
  params: { trigger: UsernameSuggestionTrigger; selectedPosition: number },
): void {
  track(EVENTS.USERNAME_SUGGESTION_SELECTED, params);
}

export function trackOTPSent(
  track: TrackFn,
  method: string,
  options?: { deliveryTime?: number; retryCount?: number; source?: OTPSource },
): void {
  const tracker = RegistrationFunnelTracker.getInstance();
  const isRegisterSource = (options?.source ?? OTPSource.REGISTER) === OTPSource.REGISTER;
  if (isRegisterSource) {
    tracker.addStep(RegistrationStep.OTP_SENT);
  }
  const computedRetryCount = isRegisterSource ? tracker.incrementAttempt("otp_sent") : undefined;

  track(EVENTS.OTP_REQUEST_RESULT, {
    method,
    step: RegistrationStep.OTP_SENT,
    deliveryTime: options?.deliveryTime,
    retryCount: options?.retryCount || computedRetryCount,
    source: options?.source,
    success: true,
  });
}

export function trackOTPVerified(
  track: TrackFn,
  method: string,
  options?: { attemptsCount?: number; timeToVerify?: number; source?: OTPSource },
): void {
  const tracker = RegistrationFunnelTracker.getInstance();
  const isRegisterSource = (options?.source ?? OTPSource.REGISTER) === OTPSource.REGISTER;
  if (isRegisterSource) {
    tracker.addStep(RegistrationStep.OTP_VERIFY);
  }

  track(EVENTS.OTP_VERIFY_RESULT, {
    method,
    step: RegistrationStep.OTP_VERIFY,
    attemptsCount: options?.attemptsCount || 1,
    timeToVerify: options?.timeToVerify,
    source: options?.source,
    success: true,
  });
}

export function trackOTPRequestFailed(track: TrackFn, error: unknown, options?: { source?: OTPSource }): void {
  const otpTracker = RegistrationFunnelTracker.getInstance();
  const isRegisterSource = (options?.source ?? OTPSource.REGISTER) === OTPSource.REGISTER;

  const metadata: OTPFailedAttemptMetadata = {
    method: OTPMethod.SMS,
    step: RegistrationStep.OTP_SENT,
    error,
    attemptsCount: isRegisterSource ? otpTracker.incrementAttempt("otp_request") : 1,
    source: options?.source,
  };
  track(EVENTS.OTP_REQUEST_RESULT, { ...metadata, success: false });
}

export function trackOTPFailedAttempt(track: TrackFn, error: unknown, options?: { source?: OTPSource }): void {
  const failTracker = RegistrationFunnelTracker.getInstance();
  const isRegisterSource = (options?.source ?? OTPSource.REGISTER) === OTPSource.REGISTER;
  const metadata = {
    method: OTPMethod.SMS,
    step: RegistrationStep.OTP_VERIFY,
    attemptsCount: isRegisterSource ? failTracker.incrementAttempt("otp_verify_fail") : 1,
    error,
    source: options?.source,
    success: false,
  };

  track(EVENTS.OTP_VERIFY_RESULT, metadata);
}

export function trackOTPRequest(track: TrackFn, method: string, options?: { source?: OTPSource }): void {
  track(EVENTS.OTP_REQUEST, {
    method,
    source: options?.source,
  });
}

export async function trackRegistrationComplete(
  track: TrackFn,
  method: RegistrationMethod,
  options?: { referralCode?: string },
): Promise<void> {
  try {
    // Prefer the code the API request actually carried; storage may already be cleared
    const referralCode = options?.referralCode ?? getReferralCode();
    const tracker = RegistrationFunnelTracker.getInstance();
    tracker.addStep(RegistrationStep.REGISTRATION_COMPLETE);

    track(EVENTS.REGISTRATION_RESULT, {
      method,
      step: RegistrationStep.REGISTRATION_COMPLETE,
      hasReferralCode: !!referralCode,
      referralCode,
      totalTimeToComplete: Math.floor(tracker.getTotalTime() / 1000),
      stepsCompleted: tracker.getCompletedSteps(),
      success: true,
    });
  } catch (error) {
    logger._console.error("[analytics] Failed to complete registration tracking:", error);
  }
}

export function trackRegistrationFailed(track: TrackFn, method: RegistrationMethod, error: unknown): void {
  const referralCode = getReferralCode();
  const tracker = RegistrationFunnelTracker.getInstance();

  track(EVENTS.REGISTRATION_RESULT, {
    method,
    hasReferralCode: !!referralCode,
    referralCode,
    success: false,
    error,
    totalTimeToComplete: Math.floor(tracker.getTotalTime() / 1000),
  });
}

// ===== Login / Auth =====

export function trackLoginClick(track: TrackFn, source: AuthSource): void {
  track(EVENTS.CLICK_LOGIN, { source });
}

export function trackGoogleAuthClick(track: TrackFn, source: GoogleAuthSource): void {
  track(EVENTS.CLICK_GOOGLE_AUTH, { source });
}

export function trackGooglePromptShown(track: TrackFn): void {
  track(EVENTS.GOOGLE_PROMPT_SHOWN, { source: "register", path: "otp_failed" });
}

export function trackGooglePromptTap(track: TrackFn): void {
  track(EVENTS.GOOGLE_PROMPT_TAP, { source: "register", path: "otp_failed" });
}

export function trackAuthClick(track: TrackFn, source: AuthSource, requireRegister: boolean = true): void {
  const hasSavedPhoneNumber = getStoredCredential()?.clientId;
  if (hasSavedPhoneNumber) {
    trackLoginClick(track, source);
  } else {
    if (requireRegister) {
      trackRegisterClick(track, source);
    } else {
      trackLoginClick(track, source);
    }
  }
}

export function trackLogin(
  track: TrackFn,
  authMode: MemberSettingAuthMode,
  options?: { grantType?: LoginGrantType },
): void {
  track(EVENTS.LOGIN, {
    authMode,
    grantType: options?.grantType?.toString(),
  });
}

export function trackLoginFormSubmit(
  track: TrackFn,
  authMode: MemberSettingAuthMode,
  grantType: LoginGrantType,
  options?: { timeSpentOnForm?: number },
): void {
  track(EVENTS.LOGIN_FORM_SUBMIT, {
    authMode,
    grantType: grantType?.toString(),
    timeSpentOnForm: options?.timeSpentOnForm,
  });
}

export function trackLoginFailed(
  track: TrackFn,
  error: string | undefined,
  options?: { authMode?: MemberSettingAuthMode; grantType?: LoginGrantType },
): void {
  track(EVENTS.LOGIN_FAILED, {
    authMode: options?.authMode,
    grantType: options?.grantType?.toString(),
    error,
  });
}

// ===== Deposit =====

export function trackDepositClick(
  track: TrackFn,
  source: DepositSource | null | undefined,
  options?: { accountBalance?: number },
): void {
  const tracker = DepositFunnelTracker.getInstance();
  tracker.startDeposit();
  tracker.addStep(DepositStep.CLICK_DEPOSIT);

  track(EVENTS.CLICK_DEPOSIT, {
    source: source ?? null,
    previousPage: window.location.pathname,
    accountBalance: options?.accountBalance,
  });
}

export function trackDepositMethodGroup(
  track: TrackFn,
  groupId: string,
  groupName: string,
  source?: DepositSource | null,
  options?: { preselected?: boolean },
): void {
  const tracker = DepositFunnelTracker.getInstance();
  tracker.addStep(DepositStep.SELECT_GROUP);
  tracker.setSelectedData("groupId", groupId);
  tracker.setSelectedData("groupName", groupName);

  track(EVENTS.SELECT_DEPOSIT_METHOD_GROUP, {
    source: source ?? null,
    step: DepositStep.SELECT_GROUP,
    groupId,
    groupName,
    // Lets funnels exclude groups picked by quick deposit.
    ...(options?.preselected ? { preselected: true } : {}),
  });
}

export function trackDepositMethodIndividual(
  track: TrackFn,
  methodId: string,
  methodName: string,
  options?: {
    source?: DepositSource;
    methodKey?: string;
    groupId?: string;
    isRecommended?: boolean;
    fees?: number;
    processingTime?: string;
  },
): void {
  const tracker = DepositFunnelTracker.getInstance();
  tracker.addStep(DepositStep.SELECT_METHOD);
  tracker.setSelectedData("methodId", methodId);
  tracker.setSelectedData("methodName", methodName);

  track(EVENTS.SELECT_DEPOSIT_METHOD_INDIVIDUAL, {
    source: options?.source,
    step: DepositStep.SELECT_METHOD,
    methodId,
    methodName,
    methodKey: options?.methodKey,
    groupId: options?.groupId,
    isRecommended: options?.isRecommended,
    fees: options?.fees,
    processingTime: options?.processingTime,
  });
}

export function trackAutoFastTriggered(
  track: TrackFn,
  autoSelectedMethod: string,
  options?: {
    source?: DepositSource;
    originalMethod?: string;
    reason?: string;
    timeSaved?: string;
  },
): void {
  track(EVENTS.AUTO_FAST_TRIGGERED, {
    source: options?.source,
    step: DepositStep.SELECT_METHOD,
    originalMethod: options?.originalMethod,
    autoSelectedMethod,
    reason: options?.reason || "auto_selection",
    timeSaved: options?.timeSaved,
  });
}

export function trackDepositAmountSelect(
  track: TrackFn,
  amount: number,
  options?: {
    source?: DepositSource;
    selectedMethod?: string;
    isQuickAmount?: boolean;
    bonusOffered?: number;
    currency?: string;
  },
): void {
  const tracker = DepositFunnelTracker.getInstance();
  tracker.addStep(DepositStep.SELECT_AMOUNT);
  tracker.setSelectedData("amount", amount);

  track(EVENTS.SELECT_DEPOSIT_AMOUNT, {
    source: options?.source,
    step: DepositStep.SELECT_AMOUNT,
    amount,
    selectedMethod: options?.selectedMethod,
    bonusOffered: options?.bonusOffered,
    currency: options?.currency,
  });
}

export function trackTopUpFailed(track: TrackFn, error: unknown, options?: DepositCreateResultOptions): void {
  track(EVENTS.DEPOSIT_CREATE_RESULT, {
    source: options?.source,
    currency: options?.currency,
    error,
    success: false,
    methodId: options?.methodId,
    methodName: options?.methodName,
    orderRef: options?.orderRef,
    depositGroupId: options?.depositGroupId,
    depositGroupName: options?.depositGroupName,
    methodKey: options?.methodKey,
    amount: options?.amount,
    providerKey: options?.providerKey,
    paymentMethod: options?.paymentMethod,
    payDataType: options?.payDataType,
    channelSelectionMode: options?.channelSelectionMode,
    errorCode: options?.errorCode,
    iframeNewTabEligible: options?.iframeNewTabEligible,
  });
}

export function trackTopUpSubmit(
  track: TrackFn,
  amount: number,
  methodId: string,
  methodName: string,
  options?: {
    source?: DepositSource;
    currency?: string;
    estimatedProcessingTime?: string;
    channelSelectionMode?: ChannelSelectionMode;
  },
): void {
  const tracker = DepositFunnelTracker.getInstance();
  tracker.addStep(DepositStep.SUBMIT_TOP_UP);

  track(EVENTS.TOP_UP_SUBMIT, {
    source: options?.source,
    step: DepositStep.SUBMIT_TOP_UP,
    amount,
    methodId,
    methodName,
    currency: options?.currency,
    estimatedProcessingTime: options?.estimatedProcessingTime,
    channelSelectionMode: options?.channelSelectionMode,
  });
}

export type DepositCompletionTrigger = "ws" | "polling" | "reconcile";

export function trackDepositSuccess(
  track: TrackFn,
  amount: number,
  transactionId: string,
  options?: {
    source?: DepositSource;
    method?: string;
    currency?: string;
    actualProcessingTime?: number;
    methodId?: string;
    providerKey?: string;
    isFirstDeposit?: boolean;
    trigger?: DepositCompletionTrigger;
    iframeNewTabEligible?: boolean;
    /** BE marked the order as a quick deposit. */
    quickDeposit?: boolean;
  },
): void {
  if (hasSeenAndMarkDeposit(transactionId)) {
    logger._console.log("[analytics] Skipping duplicate deposit_success for", transactionId);
    return;
  }

  const tracker = DepositFunnelTracker.getInstance();
  tracker.addStep(DepositStep.DEPOSIT_COMPLETE);

  track(EVENTS.DEPOSIT_SUCCESS, {
    source: options?.source,
    step: DepositStep.DEPOSIT_COMPLETE,
    amount,
    methodId: options?.methodId,
    method: options?.method,
    orderRef: transactionId,
    providerKey: options?.providerKey,
    currency: options?.currency,
    actualProcessingTime: options?.actualProcessingTime,
    totalDepositTime: Math.floor(tracker.getTotalTime() / 1000),
    isFirstDeposit: options?.isFirstDeposit,
    trigger: options?.trigger,
    iframeNewTabEligible: options?.iframeNewTabEligible,
    // snake_case to match BE's quick deposit events.
    ...(options?.quickDeposit ? { quick_deposit: true } : {}),
  });
}

export function trackDepositFailed(
  track: TrackFn,
  amount: number,
  methodId: string,
  error: unknown,
  options?: {
    source?: DepositSource;
    methodName?: string;
    currency?: string;
    retryAttempt?: number;
    failurePoint?: string;
  },
): void {
  track(EVENTS.DEPOSIT_FAILED, {
    source: options?.source,
    step: DepositStep.SUBMIT_DEPOSIT,
    amount,
    methodId,
    methodName: options?.methodName,
    currency: options?.currency,
    error,
    retryAttempt: options?.retryAttempt || 1,
    failurePoint: options?.failurePoint,
  });
}

export function trackCloseDepositIframe(track: TrackFn, metadata: CloseDepositIframeMetadata): void {
  if (
    metadata.reason === DepositIframeCloseReason.DEPOSIT_SUCCESS &&
    hasSeenAndMarkCloseDeposit(metadata.orderRef, metadata.reason)
  ) {
    return;
  }
  track(EVENTS.CLOSE_DEPOSIT_IFRAME, {
    reason: metadata.reason,
    orderRef: metadata.orderRef,
  });
}

export function trackOpenDepositIframe(track: TrackFn, metadata: DepositCreateResultOptions): void {
  track(EVENTS.OPEN_DEPOSIT_IFRAME, {
    orderRef: metadata.orderRef,
    methodId: metadata.methodId,
    methodName: metadata.methodName,
    methodKey: metadata.methodKey,
    providerKey: metadata.providerKey,
    depositGroupId: metadata.depositGroupId,
    depositGroupName: metadata.depositGroupName,
    paymentMethod: metadata.paymentMethod,
    amount: metadata.amount,
    payDataType: metadata.payDataType,
  });
}

export function trackDepositCreateSuccess(track: TrackFn, amount: number, options: DepositCreateResultOptions): void {
  track(EVENTS.DEPOSIT_CREATE_RESULT, {
    source: options?.source,
    currency: options?.currency,
    success: true,
    amount,
    methodId: options?.methodId,
    methodName: options?.methodName,
    orderRef: options?.orderRef,
    depositGroupId: options?.depositGroupId,
    depositGroupName: options?.depositGroupName,
    methodKey: options?.methodKey,
    providerKey: options?.providerKey,
    paymentMethod: options?.paymentMethod,
    payDataType: options?.payDataType,
    channelSelectionMode: options?.channelSelectionMode,
    iframeNewTabEligible: options?.iframeNewTabEligible,
  });
}

export function trackCloseDepositCustom(track: TrackFn, metadata: CloseDepositNativeMetadata): void {
  if (
    metadata.reason === DepositNativeCloseReason.DEPOSIT_SUCCESS &&
    hasSeenAndMarkCloseDeposit(metadata.orderRef, metadata.reason)
  ) {
    return;
  }
  track(EVENTS.CLOSE_DEPOSIT_CUSTOM, {
    orderRef: metadata.orderRef,
    reason: metadata.reason,
  });
}

export function trackOpenDepositCustom(track: TrackFn, metadata: DepositCreateResultOptions): void {
  track(EVENTS.OPEN_DEPOSIT_CUSTOM, {
    orderRef: metadata.orderRef,
    methodId: metadata.methodId,
    methodName: metadata.methodName,
    methodKey: metadata.methodKey,
    providerKey: metadata.providerKey,
    depositGroupId: metadata.depositGroupId,
    depositGroupName: metadata.depositGroupName,
    paymentMethod: metadata.paymentMethod,
    amount: metadata.amount,
    payDataType: metadata.payDataType,
  });
}

export function trackDepositGroupsEmpty(track: TrackFn, metadata: DepositGroupsEmptyMetadata): void {
  track(EVENTS.DEPOSIT_GROUPS_EMPTY, metadata);
}

export function trackDepositMethodsEmpty(track: TrackFn, metadata: DepositMethodsEmptyMetadata): void {
  track(EVENTS.DEPOSIT_METHODS_EMPTY, metadata);
}

function slipUploadMetadata({ file, detectedType, attemptNumber, orderRef }: SlipUploadAttemptMetadata) {
  return {
    file_mime: file.type,
    file_ext: file.name.includes(".") ? file.name.split(".").pop()?.toLowerCase() : undefined,
    file_size_kb: Math.round(file.size / 1024),
    file_detected_type: detectedType,
    attempt_number: attemptNumber,
    brand: envConfig?.brand?.key,
    market: envConfig?.countryCode,
    order_id: orderRef,
  };
}

export function trackSlipUploadFailed(
  track: TrackFn,
  { reason, httpStatus, errorMessage, providerKey, retry, ...attempt }: SlipUploadFailedMetadata,
): void {
  track(EVENTS.SLIP_UPLOAD_FAILED, {
    reason,
    ...slipUploadMetadata(attempt),
    http_status: httpStatus,
    error_message: errorMessage,
    provider_key: providerKey,
    retry,
  });
}

export function trackSlipUploadSuccess(track: TrackFn, attempt: SlipUploadAttemptMetadata): void {
  track(EVENTS.SLIP_UPLOAD_SUCCESS, slipUploadMetadata(attempt));
}

// ===== PGW Redirect Auto-Close =====

export function trackRedirectAutocloseResult(track: TrackFn, metadata: RedirectAutocloseResultMetadata): void {
  track(EVENTS.REDIRECT_AUTOCLOSE_RESULT, metadata);
}

// ===== Gameplay =====

function gameMetadata(game: Partial<GameModel>) {
  return {
    gameId: game?.id,
    gameName: game?.name,
    gameProvider: game?.providerRef,
    gameProviderId: game?.gameProviderId,
    gameType: game?.type,
    gameCategory: game?.category?.name ?? null,
    gameDescription: game?.description,
    gameRank: game?.rank,
    gameStatus: game?.status,
    gamePrimaryImageUrl: game?.primaryImage?.url,
    isMaintenance: game?.isMaintenance,
  };
}

interface GameOpenSourceOptions {
  openSource?: string;
  openSourceCategory?: string;
  openPosition?: number;
  gamePosition?: number;
  surpriseSessionId?: string;
}

function gameOpenSourceMetadata(options?: GameOpenSourceOptions) {
  return {
    openSource: options?.openSource ?? GAME_SOURCE.OTHER,
    openSourceCategory: options?.openSourceCategory ?? "",
    openPosition: options?.openPosition,
    ...(options?.openSource === GAME_SOURCE.SURPRISE_ME && {
      gamePosition: options?.gamePosition,
      surpriseSessionId: options?.surpriseSessionId,
    }),
  };
}

export function trackGameOpened(
  track: TrackFn,
  game: Partial<GameModel>,
  options?: GameOpenSourceOptions & { source?: string; searchTerm?: string },
): void {
  track(EVENTS.GAME_OPENED, {
    ...gameMetadata(game),
    source: options?.source,
    searchTerm: options?.searchTerm,
    ...gameOpenSourceMetadata(options),
  });
}

export function trackGameOpenTriggered(
  track: TrackFn,
  game: Partial<GameModel>,
  options?: GameOpenSourceOptions & { source?: string; searchTerm?: string },
): void {
  track(EVENTS.GAME_OPEN_TRIGGERED, {
    ...gameMetadata(game),
    source: options?.source,
    searchTerm: options?.searchTerm,
    ...gameOpenSourceMetadata(options),
  });
}

export function trackGameSearch(
  track: TrackFn,
  searchTerm: string | undefined,
  options?: { resultCount?: number },
): void {
  track(EVENTS.GAME_SEARCH, {
    searchTerm,
    resultCount: options?.resultCount,
  });
}

export function trackGameSearchSelect(
  track: TrackFn,
  game: Partial<GameModel>,
  options?: { source?: string; searchTerm?: string },
): void {
  track(EVENTS.GAME_SEARCH_SELECT, {
    game,
    gameCategory: game?.category?.name ?? null,
    source: options?.source,
    searchTerm: options?.searchTerm,
  });
}

export function trackGameClosed(track: TrackFn, game: Partial<GameModel>, options?: GameOpenSourceOptions): void {
  track(EVENTS.GAME_CLOSED, { ...gameMetadata(game), ...gameOpenSourceMetadata(options) });
}

export function trackGameExit(track: TrackFn, gameInfo: GameExitParams): void {
  track(EVENTS.GAME_EXIT, {
    gameId: gameInfo.id,
    gameName: gameInfo.name,
    gameProviderSourceRef: gameInfo.providerSourceRef,
    gameProviderCode: gameInfo.providerGameCode,
    gameCategory: gameInfo.category ?? null,
    balanceChanged: gameInfo.balanceChanged,
    balanceDelta: gameInfo.balanceDelta,
    ...gameOpenSourceMetadata(gameInfo),
  });
}

export function trackGameError(
  track: TrackFn,
  error: unknown,
  gameProviderId?: string,
  options?: GameOpenSourceOptions & { gameCategory?: string | null },
): void {
  track(EVENTS.GAME_ERROR, {
    error,
    gameProviderId,
    gameCategory: options?.gameCategory ?? null,
    ...gameOpenSourceMetadata(options),
  });
}

export function trackGameContentVisibilityFallbackClick(track: TrackFn): void {
  track(EVENTS.GAME_CONTENT_VISIBILITY_FALLBACK_CLICK, {});
}

export function trackSessionTimeout(track: TrackFn): void {
  track(EVENTS.SESSION_TIMEOUT, {});
}

// ===== Surprise Me =====

interface SurpriseMeEventPayload extends SurpriseMeEventMetadata {
  /** The game on screen when the event fired. Absent before the first batch lands. */
  game?: Partial<GameModel>;
}

function surpriseMeMetadata({ game, ...rest }: SurpriseMeEventPayload) {
  return { ...rest, ...(game ? gameMetadata(game) : {}) };
}

/** Tile impression. Carries no session or game: neither exists until the tap. */
export function trackSurpriseMeTileShown(track: TrackFn): void {
  track(EVENTS.SURPRISE_ME_TILE_SHOWN, {});
}

/** Fires before the first batch resolves, so it carries position 1 and no game. */
export function trackSurpriseMeTileClicked(track: TrackFn, surpriseSessionId: string): void {
  track(EVENTS.SURPRISE_ME_TILE_CLICKED, { surprise_session_id: surpriseSessionId, game_position: 1 });
}

/** Carries the game the player moved TO, at its post-increment position. */
export function trackSurpriseMeNext(track: TrackFn, payload: SurpriseMeEventPayload): void {
  track(EVENTS.SURPRISE_ME_NEXT, surpriseMeMetadata(payload));
}

/** Carries the game the player moved back TO, at its post-decrement position. */
export function trackSurpriseMeBack(track: TrackFn, payload: SurpriseMeEventPayload): void {
  track(EVENTS.SURPRISE_ME_BACK, surpriseMeMetadata(payload));
}

/**
 * Paired with the existing `act:game_open_triggered` carrying
 * `openSource = surprise_me`, which `useGameGateway` fires from the same tap.
 */
export function trackSurpriseMePlayNow(track: TrackFn, payload: SurpriseMeEventPayload): void {
  track(EVENTS.SURPRISE_ME_PLAY_NOW, surpriseMeMetadata(payload));
}

/** User-initiated close only. An error-driven close is not a player action. */
export function trackSurpriseMeClosed(track: TrackFn, payload: SurpriseMeEventPayload): void {
  track(EVENTS.SURPRISE_ME_CLOSED, surpriseMeMetadata(payload));
}

// ===== Promotions =====

export function trackPromoClaimAttempt(track: TrackFn, promoType: WalletTxMethod | "refer-ball"): void {
  track(EVENTS.PROMO_CLAIM_ATTEMPT, { promoType });
}

export function trackPromoClaimSuccess(track: TrackFn, promoType: WalletTxMethod | "refer-ball"): void {
  track(EVENTS.PROMO_CLAIM_SUCCESS, { promoType });
}

export function trackPromoIssued(track: TrackFn, promoType: WalletTxMethod, amount: number): void {
  track(EVENTS.PROMO_ISSUED, { promoType, amount });
}

export function trackPromoRewardApplied(track: TrackFn, promoId: string): void {
  track(EVENTS.PROMO_REWARD_APPLIED, { promoId });
}

export function trackPromoBonusExpired(track: TrackFn, promoId: string): void {
  track(EVENTS.PROMO_BONUS_EXPIRED, { promoId });
}

export function trackClickMiniBanner(track: TrackFn, metadata: ClickMiniBannerMetadata): void {
  track(EVENTS.CLICK_MINI_BANNER, metadata);
}

export function trackClickHomeBanner(track: TrackFn, metadata: ClickHomeBannerMetadata): void {
  track(EVENTS.CLICK_HOME_BANNER, metadata);
}

export function trackClickPromotionPage(track: TrackFn, metadata: ClickPromotionPageMetadata): void {
  track(EVENTS.CLICK_PROMOTION_PAGE, metadata);
}

export function trackClickPromotionItem(track: TrackFn, metadata: ClickPromotionItemMetadata): void {
  track(EVENTS.CLICK_PROMOTION_ITEM, metadata);
}

export function trackCashbackMiniPromptShown(track: TrackFn, metadata: CashbackMiniPromptShownMetadata): void {
  track(EVENTS.CASHBACK_MINI_PROMPT_SHOWN, metadata);
}

type AutoPromotionPopupType = (typeof PROMOTION_TYPE)[keyof typeof PROMOTION_TYPE];

export type AutoClaimPopupPromoType = "daily-bonus" | "daily-streak" | AutoPromotionPopupType;

export interface PromoAutoClaimPopupMetadata {
  promoType: AutoClaimPopupPromoType;
  streakCount?: number;
}

export function trackPromoAutoClaimPopup(track: TrackFn, metadata: PromoAutoClaimPopupMetadata): void {
  track(EVENTS.PROMO_AUTO_CLAIM_POPUP, metadata);
}

// ===== Canonical promotion journey events =====
// These fire alongside the legacy events above, not instead of them.

export function trackPromotionPresented(track: TrackFn, metadata: PromotionPresentedMetadata): void {
  track(EVENTS.PROMOTION_PRESENTED, metadata);
}

export function trackPromotionAction(track: TrackFn, metadata: PromotionActionMetadata): void {
  track(EVENTS.PROMOTION_ACTION, metadata);
}

export function trackPromotionClaimResult(track: TrackFn, metadata: PromotionClaimResultMetadata): void {
  track(EVENTS.PROMOTION_CLAIM_RESULT, metadata);
}

/**
 * Canonical `evt:promotion_issued`, distinct from the legacy `evt:promo_issued`.
 * `trackPromoIssued` above still fires for the cashback accrual tracker.
 */
export function trackPromotionIssued(track: TrackFn, metadata: PromotionIssuedMetadata): void {
  track(EVENTS.PROMOTION_ISSUED, metadata);
}

export function trackPromotionQueueEnded(track: TrackFn, metadata: PromotionQueueEndedMetadata): void {
  track(EVENTS.PROMOTION_QUEUE_ENDED, metadata);
}

export function trackNudgeModalPresented(track: TrackFn, metadata: NudgeModalPresentedMetadata): void {
  track(EVENTS.NUDGE_MODAL_PRESENTED, metadata);
}

// ===== Withdrawals =====

export function trackWithdrawalClick(track: TrackFn, source?: WithdrawalSource): void {
  track(EVENTS.CLICK_WITHDRAWAL, {
    source,
  });
}

export function trackWithdrawalSubmit(track: TrackFn, payload: WithdrawRequest): void {
  track(EVENTS.WITHDRAWAL_SUBMIT, { payload });
}

export function trackWithdrawalSuccess(track: TrackFn, response: WithdrawResponse): void {
  track(EVENTS.WITHDRAWAL_SUCCESS, { response });
}

export function trackWithdrawalRequestFailed(track: TrackFn, error: unknown): void {
  track(EVENTS.WITHDRAWAL_REQUEST_FAILED, { error, errorMessage: getApiErrorMessage(error) });
}

// ===== Notifications =====

export function trackNotificationInAppPromptShown(
  track: TrackFn,
  source: NotificationInAppSource = NotificationInAppSource.PRIZES_PAGE,
): void {
  const metadata: NotificationInAppPromptShownMetadata = { source };
  track(EVENTS.NOTIFICATION_INAPP_PROMPT_SHOWN, metadata);
}

export function trackNotificationInAppPromptResponse(
  track: TrackFn,
  action: NotificationAction,
  source: NotificationInAppSource,
): void {
  const metadata: NotificationInAppPromptResponseMetadata = { action, source };
  track(EVENTS.NOTIFICATION_INAPP_PROMPT_RESPONSE, metadata);
}

export function trackNotificationBrowserPromptShown(track: TrackFn, source?: NotificationInAppSource): void {
  const metadata: NotificationBrowserPromptShownMetadata = { source };
  track(EVENTS.NOTIFICATION_BROWSER_PROMPT_SHOWN, metadata);
}

export function trackNotificationBrowserPromptResponse(track: TrackFn, action: NotificationAction): void {
  const metadata: NotificationBrowserPromptResponseMetadata = { action };
  track(EVENTS.NOTIFICATION_BROWSER_PROMPT_RESPONSE, metadata);
}

export function trackNotificationSubscriptionStatus(
  track: TrackFn,
  status: NotificationPermissionState,
  source?: NotificationInAppSource,
): void {
  const metadata: NotificationSubscriptionStatusMetadata = { status, source };
  track(EVENTS.NOTIFICATION_SUBSCRIPTION_STATUS, metadata);
}

// ===== Language =====

export function trackChangeLanguage(track: TrackFn, from: string, to: string, source: LanguageChangeSource): void {
  if (from === to) return;
  track(EVENTS.CHANGE_LANGUAGE, { from, to, source });
}

// ===== Customer Support (CS) =====

export function trackCsClick(
  track: TrackFn,
  source: CsSource,
  options: { userMode: UserMode; flowStep?: CsFlowStep; targetUrl?: string },
): void {
  // No targetUrl means a chooser opens, so the channel isn't known yet.
  const metadata: CsClickMetadata = {
    source,
    channel: options.targetUrl ? resolveCsChannel(options.targetUrl) : "group",
    userMode: options.userMode,
  };
  if (options.flowStep) metadata.flowStep = options.flowStep;
  if (options.targetUrl) metadata.targetUrl = options.targetUrl;
  track(EVENTS.CLICK_CS, metadata);
}

export function trackSelectCsChannel(track: TrackFn, source: CsSource, url: string, channelName: string): void {
  const metadata: CsChannelMetadata = { source, channel: resolveCsChannel(url), channelName, targetUrl: url };
  track(EVENTS.SELECT_CS_CHANNEL, metadata);
}

export function trackContactCsEntryShown(
  track: TrackFn,
  source: CsSource,
  options: { userMode: UserMode; flowStep?: CsFlowStep },
): void {
  if (hasSeenAndMarkContactCsEntryShown(source, options.userMode, options.flowStep)) return;
  const metadata: ContactCsEntryShownMetadata = { source, userMode: options.userMode };
  if (options.flowStep) metadata.flowStep = options.flowStep;
  track(EVENTS.CONTACT_CS_ENTRY_SHOWN, metadata);
}

// ===== Header =====

export function trackHeaderShown(track: TrackFn, userMode: UserMode, buttonsShown: string[]): void {
  if (hasSeenAndMarkHeaderShown(userMode, buttonsShown)) return;
  const metadata: HeaderShownMetadata = { userMode, buttonsShown };
  track(EVENTS.HEADER_SHOWN, metadata);
}

// ===== FTD incentive experiment (KZ-7296) =====

// Once per session per deadline
export function trackFtdCountdownShown(track: TrackFn, metadata: FtdCountdownMetadata): void {
  runWhenProvidersReady(() => {
    if (hasSeenAndMarkOncePerSession(`analytics:ftd_countdown_shown:${metadata.expireAt}`)) return;
    track(EVENTS.FTD_COUNTDOWN_SHOWN, metadata);
  });
}

export function trackFtdCountdownExpired(track: TrackFn, metadata: FtdCountdownMetadata): void {
  runWhenProvidersReady(() => {
    if (hasSeenAndMarkOncePerSession(`analytics:ftd_countdown_expired:${metadata.expireAt}`)) return;
    track(EVENTS.FTD_COUNTDOWN_EXPIRED, metadata);
  });
}

export function trackOpenGetMoreBallsTab(track: TrackFn, metadata: OpenGetMoreBallsTabMetadata): void {
  track(EVENTS.OPEN_GET_MORE_BALLS_TAB, metadata);
}

// ===== VIP quick deposit experiment =====

export function trackVipQuickDepositShown(track: TrackFn, metadata: VipQuickDepositMetadata): void {
  runWhenProvidersReady(() => {
    track(EVENTS.VIP_QUICK_DEPOSIT_SHOWN, metadata);
  });
}

export function trackVipQuickDepositClicked(track: TrackFn, metadata: VipQuickDepositClickedMetadata): void {
  track(EVENTS.VIP_QUICK_DEPOSIT_CLICKED, metadata);
}

export function trackVipQuickDepositPreselectSkipped(track: TrackFn, metadata: VipQuickDepositSkippedMetadata): void {
  track(EVENTS.VIP_QUICK_DEPOSIT_PRESELECT_SKIPPED, metadata);
}

/** Fires `submitted`, plus `amount_changed` / `method_changed` when the player edited the suggestion. */
export function trackVipQuickDepositSubmitted(track: TrackFn, metadata: VipQuickDepositSubmittedMetadata): void {
  track(EVENTS.VIP_QUICK_DEPOSIT_SUBMITTED, metadata);
  if (metadata.amount_changed) {
    track(EVENTS.VIP_QUICK_DEPOSIT_AMOUNT_CHANGED, {
      usual_amount: metadata.usual_amount,
      final_amount: metadata.final_amount,
    });
  }
  if (metadata.method_changed) {
    track(EVENTS.VIP_QUICK_DEPOSIT_METHOD_CHANGED, {
      usual_method: metadata.usual_method,
      final_method: metadata.final_method,
      usual_group_id: metadata.usual_group_id,
      final_group_id: metadata.final_group_id,
    });
  }
}

// ===== Promotion user journey (KZ-6739 / TK-815 / TK-903) =====
// `act:promotion_action` itself converged onto the canonical `trackPromotionAction`
// (see the "Canonical promotion journey events" section above) — TK-815 fields with
// no canonical equivalent (`referralCode`, `screenContext`) now live as optional
// fields on `PromotionActionMetadata`. Only `evt:share_confirmed` stays TK-815-specific
// below, since nothing else emits it.

/**
 * Fires when `navigator.share` resolves (not rejected with AbortError). Dismissing the
 * sheet is just the absence of this event. Some OS/channels resolve as soon as the
 * sheet opens, so this isn't a guaranteed "actually shared" — TK-815 knows that.
 */
export function trackShareConfirmed(
  track: TrackFn,
  meta: {
    screenContext: PromotionScreenContext;
    promotionType: string;
    referralCode?: string;
    channel: ShareChannel;
  },
): void {
  track(EVENTS.SHARE_CONFIRMED, {
    promotionType: meta.promotionType,
    screenContext: meta.screenContext,
    referralCode: meta.referralCode,
    channel: meta.channel,
  });
}

export function trackReferBallsLastBallUsed(track: TrackFn): void {
  track(EVENTS.REFER_BALLS_LAST_BALL_USED, {});
}

/**
 * Fires when a visitor lands on a referral link. Deduped per session per code since
 * `/r/<code>` redirects to `?r=<code>` and would otherwise double-fire.
 *
 * TK-815 moved this event to Backend; kept client-side for now since BE has nothing to
 * observe before registration. Drop once BE emits its own. TK-815's `channel` field isn't
 * implementable here — `document.referrer` is empty for most app-to-app shares.
 */
export function trackReferralLinkOpened(
  track: TrackFn,
  meta: { referralCode: string; linkShape: "path" | "query" },
): void {
  if (hasSeenAndMarkOncePerSession(`analytics:referral_link_opened:${meta.referralCode}`)) return;
  track(EVENTS.REFERRAL_LINK_OPENED, {
    referralCode: meta.referralCode,
    link_shape: meta.linkShape,
  });
}

// ===== PWA install funnel =====

// Shown more than once per session: send an impression per show so it pairs with its click.
const PWA_ENTRY_SHOWN_PER_SHOW: ReadonlySet<PwaInstallSource> = new Set(["deposit_success_nudge", "reward_toast"]);

export function trackPwaEntryShown(track: TrackFn, source: PwaInstallSource, installMethod: PwaInstallMethod): void {
  runWhenProvidersReady(() => {
    const dedupePerSession = !PWA_ENTRY_SHOWN_PER_SHOW.has(source);
    if (dedupePerSession && hasSeenAndMarkOncePerSession(`analytics:pwa_entry_shown:${source}`)) return;
    track(EVENTS.PWA_ENTRY_SHOWN, { source, install_method: installMethod });
  });
}

export function trackPwaInstallClick(track: TrackFn, source: PwaInstallSource, installMethod: PwaInstallMethod): void {
  track(EVENTS.PWA_INSTALL_CLICK, { source, install_method: installMethod });
}

export function trackPwaInstallSuccess(track: TrackFn, trigger: PwaInstallSuccessTrigger): void {
  // Dedup marks INSIDE the gate: a not-yet-ready provider must not burn the persistent guard.
  runWhenProvidersReady(() => {
    if (hasSeenAndMarkPwaInstallSuccess()) return;
    track(EVENTS.PWA_INSTALL_SUCCESS, { trigger });
  });
}
